import { identify, trackPageView } from "@utils/analytics";
import { reportUnexpectedHost } from "@utils/canonicalHost";
import { cookie } from "@utils/cookie";
import { getScrollTargetY } from "@utils/scrollToElement";
import chat from "@utils/chat";
export { wrapRootElement, wrapPageElement } from "./gatsby-shared";

const CHAT_HIDDEN_PATHS = ["/careers/"];

export const onInitialClientRender = () => {
  const netlifySplitTestCookie = cookie.read("nf_ab");
  const netlifySplitTestBranch = process.env.BRANCH;

  identify({
    netlifySplitTestCookie,
    netlifySplitTestBranch,
  });

  reportUnexpectedHost();
};

export const onRouteUpdate = ({ location, _prevLocation }) => {
  // Override gatsby-plugin-canonical-urls clientside logic for referral pages. See gatsby-ssr.js for context.
  const referralBasePath = /^\/referral\/([^]+)/;
  const isReferralPath = referralBasePath.test(location.pathname);
  if (isReferralPath) {
    const canonicalLink = document.querySelector(`link[rel='canonical']`);
    canonicalLink && canonicalLink.setAttribute(`href`, `https://www.mangomint.com/`);
  }

  // Hide chat widget on these pages, show on all other pages
  if (chat.isAvailable()) {
    if (CHAT_HIDDEN_PATHS.includes(location.pathname)) {
      chat.hide();
    } else {
      chat.show();
      chat.close();
    }
  }

  window.setTimeout(() => {
    // adding a 100ms delay because that's apparently what the gatsby-plugins do
    // see here for example: https://github.com/benjaminhoffman/gatsby-plugin-segment-js/blob/master/src/gatsby-browser.js
    trackPageView(location);
  }, 100);
};

/*
  Gatsby is inconsistently setting the document scroll position when navigating to a URL containing a
  fragment identifier, so we override the default srcoll behavior in order to fix it. We only do this
  for learning center pages since currently these are the only pages that are linked to in this way.
  We continue to allow Gatsby to handle the scroll position on all other pages in order to minimize
  any potential defects that might be introduced as a result of modifying this behavior.

  Returns one of:
    Boolean  -  true    -  Let Gatsby update the scroll position
    Boolean  -  false   -  Do not update the scroll position, handle it manually instead

  It is also possible to return an [x, y] array to specify the scroll position directly, but this
  is intentionally not used here since it results in the same scroll issue that we are trying to fix.
*/
export const shouldUpdateScroll = ({ routerProps: { location }, getSavedScrollPosition }) => {
  // Let Gatsby handle the default scroll position if...
  // Path is not a learning center path
  const isLearningCenterPath = location.pathname && location.pathname.startsWith("/learn/");
  if (!isLearningCenterPath) return true;

  // Path does not contain a fragment identifier
  const fragment = location.hash;
  if (!fragment) return true;

  // A corresponding element does not exist in the DOM
  const elementId = window.decodeURI(fragment.replace("#", ""));
  const element = document.getElementById(elementId);
  if (!element) return true;

  // Otherwise, handle the scroll position manually
  setTimeout(() => {
    window.history.scrollRestoration = "manual";

    // Before next paint, attempt to calculate element's Y position and then scroll to it
    requestAnimationFrame(() => {
      const defaultScrollPosition = [0, 0];
      const currentScrollPosition = getSavedScrollPosition(location) || defaultScrollPosition;
      const fragmentScrollPosition = [currentScrollPosition[0], getScrollTargetY(element)];

      window.scrollTo(...fragmentScrollPosition);
    });
  }, 100);

  return false;
};
